import React from 'react';
import { Page, Document, StyleSheet, Text, View } from '@react-pdf/renderer';
import PdfHeader from './components/reusable/PdfHeader';
import PageNumber from './components/reusable/PageNumber';
import ServiceTechnicianSection from './components/reusable/ServiceTechnicianSection';
import ServicePartsSection from './components/reusable/ServicePartsSection';
import CompressorSection from './refrigeration/CompressorSection';

const styles = StyleSheet.create({
    body: {
        padding: 15,
        paddingBottom: 25,
    },
    section: {
        marginTop: 8,
        padding: 6,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#cccccc',
        borderRadius: 5,
        backgroundColor: '#f8f9fa',
    },
    sectionTitle: {
        fontSize: 11,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 6,
        paddingBottom: 3,
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: '#dee2e6',
        color: '#495057',
        textAlign: 'center',
    },
    refrigerationChecklist: {
        marginTop: 8,
        padding: 6,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#cccccc',
        borderRadius: 5,
        backgroundColor: '#f8f9fa',
    },
    checklistGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
    },
    checklistItem: {
        width: '50%',
        flexDirection: 'row',
        marginBottom: 3,
        alignItems: 'center',
    },
    checkboxContainer: {
        width: 8,
        height: 8,
        borderWidth: 0.5,
        borderStyle: 'solid',
        borderColor: '#000000',
        marginRight: 4,
        textAlign: 'center',
    },
    checkboxLabel: {
        fontSize: 8,
        fontFamily: 'Helvetica',
        color: '#212529',
        flex: 1,
    },
    textContent: {
        fontSize: 9,
        fontFamily: 'Helvetica',
        color: '#212529',
        lineHeight: 1.3,
        textAlign: 'left',
    },
    notesSection: {
        minHeight: 40,
    },
});

interface RefrigerationPdfProps {
    data: {
        baseSheet: any;
        refForm: any;
        compressors: any[];
        parts: any[];
        techs: any[];
    };
    user: any;
}

const RefrigerationPdf: React.FC<RefrigerationPdfProps> = ({ data, user }) => {
    const { baseSheet, refForm, compressors, parts, techs } = data;

    const Checkbox = ({ checked }: { checked: boolean }) => (
        <View style={styles.checkboxContainer}>
            <Text style={{ backgroundColor: '#000000', color: '#ffffff', fontSize: 6 }}>
                {checked ? 'X' : ''}
            </Text>
        </View>
    );

    const sheetForHeader = {
        ...baseSheet,
        ...refForm,
    };

    return (
        <Document>
            <Page size="A4" style={styles.body}>
                <PdfHeader company={baseSheet.company} sheet={sheetForHeader} />

                <View style={styles.refrigerationChecklist}>
                    <Text style={styles.sectionTitle}>Refrigeration Checklist</Text>
                    <View style={styles.checklistGrid}>
                        <View style={styles.checklistItem}>
                            <Checkbox checked={refForm.vacuum} />
                            <Text style={styles.checkboxLabel}>Vacuum Control Panel(s)</Text>
                        </View>
                        <View style={styles.checklistItem}>
                            <Checkbox checked={refForm.crankHeater} />
                            <Text style={styles.checkboxLabel}>Crank Case Heaters</Text>
                        </View>
                        <View style={styles.checklistItem}>
                            <Checkbox checked={refForm.doors} />
                            <Text style={styles.checkboxLabel}>Check Bypass Doors</Text>
                        </View>
                        <View style={styles.checklistItem}>
                            <Checkbox checked={refForm.contactors} />
                            <Text style={styles.checkboxLabel}>Inspect Contactors</Text>
                        </View>
                        <View style={styles.checklistItem}>
                            <Checkbox checked={refForm.control} />
                            <Text style={styles.checkboxLabel}>Check Controls (Calibration, Operation)</Text>
                        </View>
                        <View style={styles.checklistItem}>
                            <Checkbox checked={refForm.circuit} />
                            <Text style={styles.checkboxLabel}>Check Failure Circuit Operation</Text>
                        </View>
                    </View>
                </View>

                {compressors && compressors.length > 0 && (
                    <CompressorSection compressors={compressors} />
                )}

                {(typeof refForm.notes === 'string' && refForm.notes.trim() !== '') && (
                    <View style={[styles.section, styles.notesSection]}>
                        <Text style={styles.sectionTitle}>Notes</Text>
                        <Text style={styles.textContent}>{refForm.notes}</Text>
                    </View>
                )}

                {techs && techs.length > 0 && (
                    <ServiceTechnicianSection techs={techs} />
                )}

                {parts && parts.length > 0 && (
                    <ServicePartsSection parts={parts} />
                )}

                <PageNumber sheetType="Refrigeration" />
            </Page>
        </Document>
    );
};

export default RefrigerationPdf;
